import React, { Component } from 'react';
import { graphql, withApollo } from 'react-apollo';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';
import { compose, mapProps } from 'recompose';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';

import Tooltip from 'components/common/Tooltip/Tooltip';
import Button from 'components/common/Buttons/Button';
import { showErrorMessage } from 'components/common/Alert/Alert';
import { isAssessmentReadOnly as isAssessmentReadOnlySelector, getIdFromUrlParams } from 'selectors/assessment/selector';
import { listFieldsState, getInvalidTemplateFields } from 'graphql/operations';
import { hasUnansweredDDERequiredFields } from 'selectors/templates/selector';
import SubmitTemplateModal from 'components/threepp/assessment/Footer/SubmitTemplateModal';
import { DeviceWidth } from 'utils/constants';
import { InputFieldsValidationModal } from './InputFieldsValidationModal';

import './Footer.scss';

const styles = () => ({
  templateActions: {
    height: '46px'
  },
  templateFooter: {
    marginRight: '16.5vw'
  },
  actionBlock: {
    display: 'flex',
    justifyContent: 'flex-end'
  }
});

export class TemplateFooter extends Component {
  state = {
    showSubmitModal: false,
    showValidationErrorModal: false,
    isMobile: false
  };

  static propTypes = {
    t: PropTypes.func.isRequired,
    // TODO: Fix this...
    // eslint-disable-next-line react/forbid-prop-types
    template: PropTypes.object.isRequired
  };

  resize = () => this.setState({ isMobile: window.innerWidth <= DeviceWidth.MOBILE });

  componentDidMount() {
    this.resize();
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  toggleSubmitModal = () => {
    this.setState(prevState => ({ showSubmitModal: !prevState.showSubmitModal }));
  };

  toggleValidationErrorModal = () => {
    this.setState(prevState => ({ showValidationErrorModal: !prevState.showValidationErrorModal }));
  };

  handleSubmit = async () => {
    const { apolloClient, template, t } = this.props;

    try {
      const res = await apolloClient.query({
        query: getInvalidTemplateFields,
        variables: { templateId: template.id, recordId: template.recordId },
        fetchPolicy: NETWORK_ONLY
      });
      const { hasValidationErrors } = res.data.getInvalidTemplateFields;

      if (hasValidationErrors) {
        this.setState({ showValidationErrorModal: true });
      } else {
        this.setState({ showSubmitModal: true });
      }
    } catch (err) {
      showErrorMessage(t('newRecord.validations.unexpected'));
    }
  };

  cancelNewRecord = () => {
    window.location.reload(true);
  };

  render() {
    const { t, template, isAssessmentReadOnly, attachmentUploadCount, templateUnansweredDDERequiredFields, classes, templateLanguages } = this.props;
    const { isMobile, showValidationErrorModal, showSubmitModal } = this.state;

    let submitDisabled = false;
    let disabledReason = '';
    if (isAssessmentReadOnly) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.assessmentReadOnly');
    } else if (attachmentUploadCount > 0) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.uploadAttachment');
      // TODO: This is a temporary solution to enforce that a record must have at least one answered question to be submitted
    } else if (template.metadata.answeredFields < 1) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.atLeastOne');
    } else if (template.metadata.unansweredRequiredFields > 0 || templateUnansweredDDERequiredFields) {
      submitDisabled = true;
      disabledReason = t('assessment.submit.hasPendingRequiredFields');
    }

    return (
      <div className={`footer-actions ${classes.templateActions} ${isAssessmentReadOnly ? 'read-only' : ''}`}>
        <div className={`${!isMobile ? classes.templateFooter : 'col-sm-8'}`}>
          <span className={classes.actionBlock}>
            <Button testId="template-clear-button" className="clear-btn template-btn" buttonStyle="text" onClick={this.cancelNewRecord}>
              {t('assessment.clearAssessment')}
            </Button>
            <Tooltip title={disabledReason}>
              {/* Tooltip requires a non-disabled child */}
              <div>
                <InputFieldsValidationModal toggle={this.toggleValidationErrorModal} isOpen={showValidationErrorModal} isNewRecord />
                <SubmitTemplateModal
                  toggle={this.toggleSubmitModal}
                  isOpen={showSubmitModal}
                  templateId={template.id}
                  recordId={template.recordId}
                  templateLanguageId={template.languageId}
                  templateLanguages={templateLanguages}
                />
                <Button
                  testId="template-submit-button"
                  className="submit-btn template-btn"
                  disabled={submitDisabled}
                  buttonStyle="primary"
                  onClick={this.handleSubmit}
                >
                  {t('assessment.completeAssessment')}
                </Button>
              </div>
            </Tooltip>
          </span>
        </div>
      </div>
    );
  }
}

export const TemplateFooterHOC = compose(
  withApollo,
  withRouter,
  withTranslation(),
  withStyles(styles(), { name: 'classes' }),
  graphql(listFieldsState, { name: 'listFieldsState' }),
  mapProps(props => {
    const { classes, template, templateLanguages } = props;
    return {
      t: props.t,
      classes,
      apolloClient: props.client,
      history: props.history,
      template,
      templateLanguages,
      actions: props.actions,
      templateUnansweredDDERequiredFields: hasUnansweredDDERequiredFields(props),
      isAssessmentReadOnly: isAssessmentReadOnlySelector(props),
      attachmentUploadCount: props.attachmentUploadCount,
      urlGuid: getIdFromUrlParams(props)
    };
  })
);

export default TemplateFooterHOC(TemplateFooter);
